import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Card, IconButton } from '@mui/material';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import EditIcon from '@mui/icons-material/Edit';
import { getUserDetails } from '../../../src/storageService';

interface SubGoal {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
}

interface GoalCardProps {
  id: string;
  title: string;
  description: string;
  type: 'Group' | 'Individual';
  applicableTo?: string;
  audienceNameforfac?: string;
  startdate: any;
  enddate: any;
  progress?: { done: number; total: number };
  isFaculty?: boolean;
  subgoals: SubGoal[];
}

const GoalCard: React.FC<GoalCardProps> = ({
  id,
  title,
  description,
  startdate,
  applicableTo,
  audienceNameforfac,
  enddate,
  type,
  subgoals,
  progress,
  isFaculty,
}) => {
  const navigate = useNavigate();
  const userType = getUserDetails()?.Type;
  const isOnlyFaculty = userType === 'Faculty';

  const handleArrowClick = () => {
    if (isOnlyFaculty && isFaculty) {
      navigate(`/goals/${encodeURIComponent(id)}/edit`);
    } else {
      navigate(`/goals/${encodeURIComponent(id)}`);
    }
  };

  const handleEditClick = () => {
    if (isOnlyFaculty && isFaculty) {
      // Convert subgoals to AddGoalPage format
      const convertedSubgoals = subgoals.map((s) => ({
        title: s.name,
        description: s.description,
        startDate: s.startDate ? new Date(s.startDate) : null,
        endDate: s.endDate ? new Date(s.endDate) : null,
      }));

      navigate('/goals/add-goals', {
        state: {
          id,
          title,
          description,
          type,
          applicableTo,
          audienceNameforfac,
          subgoals: convertedSubgoals,  // converted array
          progress,
          startdate,
          enddate,
        },
      });
    } else {
      navigate(`/goals/${encodeURIComponent(id)}/edit`);
    }
  };

  return (
    <Card
      sx={{
        p: 2,
        borderRadius: 2,
        boxShadow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        textAlign: 'left',
      }}
    >
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>{title}</Typography>
          <Box display="flex" alignItems="center" gap={3}>
            {isFaculty && (
              <IconButton size="small" aria-label="edit" onClick={handleEditClick}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={handleArrowClick} aria-label="go to details">
              <ArrowForwardIosRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Typography sx={{ fontSize: '14px', mt: 1, color: 'text.secondary' }}>
          {description}
        </Typography>
      </Box>

      <Box mt={2}>
        <Chip variant="outlined" size="small" label={type} style={{ backgroundColor: '#EDEEF1' }} />
        {progress && (
          <Typography variant="subtitle2" mt={1}>
            {`${progress.done}/${progress.total} on track`}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default GoalCard;
